<template>
    <div>
        <div v-if="dataLoading || loading">
            <h3 class="primary--text mb-2">Loading student data ...</h3>
            <v-progress-linear height="10"
                               color="primary"
                               rounded
                               indeterminate></v-progress-linear>
        </div>
        <div v-else>
            <psi-page-title key="page-title"
                            :title="page.title"
                            :icon="page.icon"
                            :breadcrumbs="breadcrumbs"></psi-page-title>
            <v-card class="mt-4 px-3 py-6"
                    key="card-content">
                <student-alert :student="currentStudent"
                               :screen="currentScreen"></student-alert>
                <screening-process-toolbar :title="`Student - ${currentStudent.name}`"
                                           :menuItems="toolbarItems"
                                           show-progress
                                           :progress="progress"
                                           :quickLinkItems="menuItems"
                                           :status-items="statusItems"
                                           @action="toolbarAction"
                                           @quicklink:click="scrollTo($event)">
                </screening-process-toolbar>

                <program-process-blocks class="mt-0"
                                        :current-student="student"
                                        :items="programItems">
                </program-process-blocks>

                <h4 class="accent--text text--darken-2 mt-4">
                    Tenant Sample Report
                </h4>
                <v-divider class="my-1"></v-divider>
                <screening-process-blocks v-if="hasScreen"
                                          @init="componentInitializing = false"
                                          :current-screen="currentScreen"
                                          :items="screeningItems"></screening-process-blocks>
                <div v-if="!hasScreen && !screenInProcess"
                     class="mt-3">
                    <v-btn large
                           color="accent"
                           :href="applyUrl"
                           :target="applyUrlTarget"
                           :disabled="applyUrl.length === 0">
                        <v-icon class="mr-2">mdi-clipboard-text</v-icon>
                        Start Tenant Sample Report
                        <v-icon class="ml-2">mdi-chevron-right</v-icon>
                    </v-btn>
                </div>
                <div v-if="screenInProcess">
                    <h5>
                        Your report will be available after processing
                        completes.
                    </h5>
                </div>
            </v-card>
        </div>

        <!-- Content always starts with a v-card  -->

        <v-dialog max-width="600"
                  transition="dialog-top-transition"
                  v-model="dialog"
                  overlay-color="accent"
                  overlay-opacity="0.6"
                  persistent>
            <student-start v-if="dialog"
                           @close="dialog = false"
                           :currentStudent="currentStudent"></student-start>
        </v-dialog>
        <process-print-order v-if="printDialog"
                             :order_id="$_.get(currentScreen, 'order_id', 0)"
                             v-model="printDialog">
        </process-print-order>
        <student-help :dialog.sync="helpDialog"
                      @send-message="sendMessage"></student-help>
    </div>
</template>
<script>
import {
  mapActions,
  mapGetters,
} from 'vuex';

import HomeLink from '@/mixins/homelink';

import utils from '../library/programUtils';
import StudentApply from '../mixins/apply';

export default {
    name: "student-dashboard",
    mixins: [HomeLink, StudentApply],
    components: {
        ProcessPrintOrder: () =>
            import("@components/screen/components/Print/ProcessPrintOrder.vue"),
        ProgramProcessBlocks: () =>
            import("@components/program/components/ProgramProcessBlocks.vue"),
        ScreeningProcessToolbar: () =>
            import("@components/screen/components/ScreeningProcessToolbar.vue"),
        StudentStart: () =>
            import("@components/program/components/StudentStart.vue"),
        StudentAlert: () =>
            import("@components/program/components/StudentAlert.vue"),
        ScreeningProcessBlocks: () =>
            import("@components/screen/components/ScreeningProcessBlocks.vue"),
        StudentHelp: () =>
            import("@components/program/components/StudentHelp.vue"),
    },

    data() {
        return {
            page: {
                title: "Student Dashboard",
                icon: "mdi-school",
            },
            dialog: false,
            printDialog: false,
            helpDialog: false,
            hasScreen: false,
            screenInProcess: false,
            componentInitializing: true,
        };
    },
    watch: {
        $route: {
            immediate: true,
            deep: true,
            handler(to) {
                if (this.$_.has(to, "params.id") && !this.isStudent) {
                    this.getCurrentStudent(to.params.id);
                }
            },
        },
        currentStudent() {
            if (this.$_.has(this.currentStudent, "applicant.order.id")) {
                this.getCurrentScreen(
                    this.$_.get(this.currentStudent, "applicant.order.id")
                );
            }
        },
        currentScreen(screen) {
            this.hasScreen =
                screen.status !== null &&
                Object.keys(screen.status).length !== 0 &&
                ["SCREEN_COMPLETED", "SCREEN_ERROR"].includes(
                    screen.status.status
                );

            this.screenInProcess =
                screen.status !== null &&
                Object.keys(screen.status).length !== 0 &&
                ["SCREEN_IN_PROCESS"].includes(screen.status.status);

            if (
                !this.hasScreen &&
                !this.screenInProcess &&
                this.currentStudent.id == this.authUser.id
            ) {
                this.dialog = true;
            }
        },
    },
    computed: {
        ...mapGetters("Programs", ["currentStudent", "dataLoading"]),
        ...mapGetters("Screens", ["currentScreen", "loading"]),
        student() {
            if (this.$_.has(this.currentScreen, "status")) {
                // pull the status from the screen, it gets udpated on pusher events
                const {
                    status: { status },
                } = this.currentScreen;
                return Object.assign(this.currentStudent, {
                    order: { status },
                });
            }
            return this.currentStudent;
        },

        toolbarItems() {
            return [
                {
                    type: "button",
                    icon: "mdi-clipboard-text",
                    tooltip: "Start Tenant Sample Report",
                    event: "student-toolbar-start",
                    disabled:
                        this.hasScreen ||
                        this.screenInProcess ||
                        this.applyUrl.length === 0,
                    enabled: true
                },
                {
                    type: "divider",
                },
                {
                    type: "button",
                    icon: "mdi-file-pdf-box",
                    tooltip: "Print",
                    event: "student-toolbar-print",
                    disabled: !this.hasScreen,
                    enabled: this.can("orders.print")
                },
                {
                    type: "button",
                    icon: this.$config("icons.help"),
                    tooltip: "Questions? Contact Us",
                    event: "student-toolbar-questions",
                    enabled:true
                },
            ].filter((item) => item.enabled);
        },
        breadcrumbs() {
            return [
                Object.assign(
                    {
                        text: "Home",
                        disabled: false,
                    },
                    this.homeUrl
                ),
                {
                    text:
                        Object.keys(this.currentStudent).length > 0
                            ? this.currentStudent.name
                            : "Student Detail",
                    disabled: true,
                    to: "#",
                },
            ];
        },
        statusItems() {
            return [
                {
                    color: this.$_.get(this.currentScreen, "status.color", ""),
                    class: "mr-4 d-none d-lg-block",
                    label: this.status,
                },
            ];
        },

        screeningItems() {
            return this.getNavItems("screen.navItems");
        },
        programItems() {
            return this.getNavItems("program.navItems", "program").map(
                (item) => {
                    item.title = this.isStudent
                        ? item.title.replace("Student", "My")
                        : item.title;
                    return item;
                }
            );
        },
        menuItems() {
            return this.hasScreen && this.status === "Report Complete"
                ? [...this.programItems, ...this.screeningItems]
                : this.programItems;
        },
        progress() {
            return {
                width: "300",
                value: utils.getProgress(
                    this.$_.get(this.currentScreen, "status.status")
                ),
            };
        },
        status() {
            return utils.getStatus(
                this.$_.get(this.currentScreen, "status.status")
            );
        },
    },
    methods: {
        ...mapActions("Programs", ["getCurrentStudent", "sendNotification"]),
        ...mapActions("Screens", ["getCurrentScreen"]),
        scrollTo(event) {
            this.$vuetify.goTo(`#${event.id}`, {
                easing: "easeInOutCubic",
                offset: 100,
                duration: 450,
            });
        },
        sendMessage(message) {
            const { name, email } = this.authUser;
            const payload = {
                from: {
                    address: email,
                    name,
                },
                subject: `Student Portal Help Request From ${name}`,
                message,
            };
            this.sendNotification(payload);
        },
        toolbarAction(action) {
            switch (action) {
                case "student-toolbar-start":
                    window.location.href = this.applyUrl;
                    break;
                case "student-toolbar-print":
                    this.printDialog = true;
                    break;
                case "student-toolbar-questions":
                    this.helpDialog = true;
                    break;
            }
        },
        getNavItems(configKey, source = "screen") {
            let navItems = {};
            this.$config(configKey)
                .filter((item) => item.visible)
                .filter((item) => item.roles.includes(this.authUser.role_name))
                .forEach((item) => (navItems[item.id] = item));

            // navItems is now an associative array for more easily access to item
            const layout = "student";
            const keys = this.$config(`${source}.processLayouts.${layout}`);

            return keys
                .filter((key) => this.$_.has(navItems, key.id))
                .map((key) => {
                    return {
                        ...navItems[key.id],
                        layout: key.layout,
                    };
                });
        },
    },
    created() {
        // if we load the dashboard as a logged in student, then we can get the current student
        if (this.isStudent) {
            this.getCurrentStudent(this.authUser.id);
        }
    },
};
</script>

<style scoped>
.fixed-toolbar {
    position: -webkit-sticky;
    position: sticky;
    top: 4.1rem;
    z-index: 2;
}
</style>

